import apiClient from './apiClient';

export type BotChannel = 'whatsapp' | 'telegram' | 'portal';
export type BotProviderKind =
  | 'meta_cloud'
  | 'twilio'
  | '360dialog'
  | 'telegram_bot_api'
  | 'maie_managed_whatsapp'
  | 'maie_managed_telegram';
export type BotStatus =
  | 'active'
  | 'pending_verification'
  | 'disabled'
  | 'failed';

export interface TenantBotSummary {
  id: string;
  tenantId: string;
  channel: BotChannel;
  providerKind: BotProviderKind;
  status: BotStatus;
  externalId: string | null;
  displayPhone: string | null;
  displayName: string | null;
  defaultLanguage: string;
  lastInboundAt: string | null;
  lastOutboundAt: string | null;
  config: Record<string, unknown>;
}

export interface RegisterBotInput {
  channel: BotChannel;
  providerKind: BotProviderKind;
  externalId: string;
  displayName?: string;
  displayPhone?: string;
  defaultLanguage?: string;
  config: Record<string, unknown>;
}

function unwrap<T>(envelope: unknown): T {
  const e = envelope as { data?: T } & T;
  return (e?.data ?? e) as T;
}

export interface BotApiError {
  /** Structured backend error code (e.g. TELEGRAM_TOKEN_INVALID), if any. */
  code?: string;
  /** Human-readable message — backend's when available, else a fallback. */
  message: string;
}

/**
 * Pull the structured `{ code, message }` a NestJS exception puts in
 * the response body out of an axios error. The apiClient rejects with
 * the raw AxiosError, so `err.message` is just "Request failed with
 * status code 409" — useless to show a user. Bot endpoints throw
 * ConflictException({ code, message }); this surfaces both.
 */
export function extractBotError(err: unknown): BotApiError {
  const data = (
    err as { response?: { data?: { code?: string; message?: string } } }
  )?.response?.data;
  if (data && (data.code || data.message)) {
    return { code: data.code, message: data.message ?? 'Request failed' };
  }
  if (err instanceof Error && err.message) return { message: err.message };
  return { message: 'Request failed' };
}

export async function listBots(): Promise<TenantBotSummary[]> {
  const res = await apiClient.get('/admin/bots');
  return unwrap<TenantBotSummary[]>(res.data);
}

export async function registerBot(
  input: RegisterBotInput,
): Promise<TenantBotSummary> {
  const res = await apiClient.post('/admin/bots', input);
  return unwrap<TenantBotSummary>(res.data);
}

/**
 * Self-serve Telegram provisioning. The clinic admin pastes the
 * BotFather token; the backend validates it via getMe, generates a
 * webhook secret, and registers the webhook automatically. Re-pasting
 * a rotated token updates the existing row in place.
 */
export async function provisionTelegramBot(input: {
  token: string;
  publicBaseUrl?: string;
}): Promise<TenantBotSummary> {
  const res = await apiClient.post('/admin/bots/telegram/provision', input);
  return unwrap<TenantBotSummary>(res.data);
}

export async function updateBot(
  id: string,
  patch: Partial<RegisterBotInput>,
): Promise<TenantBotSummary> {
  const res = await apiClient.patch(`/admin/bots/${id}`, patch);
  return unwrap<TenantBotSummary>(res.data);
}

export async function activateBot(id: string): Promise<TenantBotSummary> {
  const res = await apiClient.post(`/admin/bots/${id}/activate`, {});
  return unwrap<TenantBotSummary>(res.data);
}

export async function disableBot(id: string): Promise<TenantBotSummary> {
  const res = await apiClient.delete(`/admin/bots/${id}`);
  return unwrap<TenantBotSummary>(res.data);
}

// ---------- MAIE-managed bots ----------

export interface MaieManagedChannelStatus {
  channel: BotChannel;
  fleetConfigured: boolean;
  optedIn: boolean;
  deepLink: string | null;
  botDisplayName: string | null;
}

export interface MaieManagedStatus {
  tenantSlug: string;
  channels: MaieManagedChannelStatus[];
  directoryListed: boolean;
}

export async function getMaieManagedStatus(): Promise<MaieManagedStatus> {
  const res = await apiClient.get('/admin/bots/maie-managed/status');
  return unwrap<MaieManagedStatus>(res.data);
}

export async function setMaieManagedOptIn(input: {
  channel: BotChannel;
  enabled: boolean;
}): Promise<MaieManagedStatus> {
  const res = await apiClient.post('/admin/bots/maie-managed/opt-in', input);
  return unwrap<MaieManagedStatus>(res.data);
}

export async function setMaieManagedDirectoryListing(input: {
  listed: boolean;
}): Promise<MaieManagedStatus> {
  const res = await apiClient.post(
    '/admin/bots/maie-managed/directory-listing',
    input,
  );
  return unwrap<MaieManagedStatus>(res.data);
}

export async function configureBotWebhook(
  id: string,
  publicBaseUrl?: string,
): Promise<{
  webhookUrl: string;
  status: 'registered' | 'manual_required';
  notes: string[];
}> {
  const res = await apiClient.post(
    `/admin/bots/${id}/configure-webhook`,
    undefined,
    { params: publicBaseUrl ? { publicBaseUrl } : undefined },
  );
  return unwrap(res.data);
}

// ---------- Per-clinic bot customization ----------

export interface ClinicBotSettings {
  welcomeMessage?: string;
  awayMessage?: string;
  officeHours?: string;
  schedulingNotes?: string;
  contactInfo?: { phone?: string; email?: string; address?: string };
  customButtons?: Array<{
    label: string;
    payload: string;
    replyText?: string;
  }>;
  enabledFlows?: {
    book?: boolean;
    refill?: boolean;
    contact?: boolean;
    hours?: boolean;
  };
}

export async function getBotSettings(): Promise<ClinicBotSettings> {
  const res = await apiClient.get('/admin/bots/settings');
  return unwrap<ClinicBotSettings>(res.data);
}

export async function updateBotSettings(
  patch: Partial<ClinicBotSettings>,
): Promise<ClinicBotSettings> {
  const res = await apiClient.patch('/admin/bots/settings', patch);
  return unwrap<ClinicBotSettings>(res.data);
}

// ---------- Doctor-side share links ----------

export type ChannelShareSource = 'own' | 'maie_shared' | 'unavailable';

export interface ChannelShareLink {
  channel: BotChannel;
  source: ChannelShareSource;
  deepLink: string | null;
  displayLabel: string | null;
}

export interface ShareLinksResponse {
  clinicName: string;
  clinicSlug: string;
  channels: ChannelShareLink[];
}

export async function getShareLinks(): Promise<ShareLinksResponse> {
  const res = await apiClient.get('/communications/share-links');
  return unwrap<ShareLinksResponse>(res.data);
}

// ---------- Communications (doctor-side inbox) ----------

export type BotMessageDirection = 'inbound' | 'outbound';
export type BotMessageStatus =
  | 'received'
  | 'queued'
  | 'sent'
  | 'delivered'
  | 'read'
  | 'failed';

/**
 * Inbox segments. These replaced the standalone Leads-queue and Opt-outs
 * pages — a lead is a thread with booking intent, an opt-out is a flag on
 * the thread's contact. See docs/INBOX_CONSOLIDATION_PLAN.md.
 */
export type InboxSegment = 'needs_you' | 'booking' | 'all' | 'muted';

export type BotLeadStage =
  | 'initiated'
  | 'got_name'
  | 'got_complaint'
  | 'proposing_slots'
  | 'awaiting_confirm'
  | 'booked'
  | 'visited'
  | 'abandoned_timeout'
  | 'abandoned_refused';

/** Stages where the operator can still act on the booking. */
export const LEAD_STAGES_BOOKABLE: BotLeadStage[] = [
  'proposing_slots',
  'awaiting_confirm',
];

/** The slot a patient picked in the bot / Mini App (for prefill). */
export interface LeadChosenSlot {
  providerId: string;
  providerName?: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:mm
  durationMinutes?: number;
}

export interface BookLeadInput {
  providerId: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:mm
  duration?: number;
  appointmentType?: string;
  staffNotes?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  useExistingPatientId?: string;
}

/** Booking-funnel state attached to a thread. */
export interface ThreadLeadSummary {
  id: string;
  stage: BotLeadStage;
  botLlmDisabled: boolean;
  firstName: string | null;
  lastName: string | null;
}

export interface ThreadSummary {
  threadKey: string;
  channel: BotChannel;
  patientId: string | null;
  /** Decrypted "First Last" of the linked patient, when one is linked. */
  patientName: string | null;
  lastMessageAt: string;
  lastMessageBody: string | null;
  lastDirection: BotMessageDirection;
  unreadCount: number;
  lead: ThreadLeadSummary | null;
  /** `human` while a staff member holds the thread and the bot stays quiet. */
  handoffMode: 'bot' | 'human';
  optedOut: boolean;
  unverifiedBindingId: string | null;
  /** Anonymous patient-portal visitor — no patient row yet. */
  isGuest: boolean;
  /** Every channel this patient's conversation spans (one row per patient). */
  channels?: BotChannel[];
}

/** Lead fields the opened thread's rail needs (incl. booking prefill). */
export interface ThreadLeadDetail extends ThreadLeadSummary {
  complaint: string | null;
  preferredTime: string | null;
  preferredProviderId: string | null;
  chosenSlot: LeadChosenSlot | null;
  patientId: string | null;
  appointmentId: string | null;
}

/**
 * A pending "is this unknown number actually Sara Ali?" match. The bot stays
 * silent on the thread until staff decide.
 */
export interface PendingBindingDetail {
  id: string;
  suggestedPatient: { id: string; name: string } | null;
}

/** Per-thread state for the context rail. */
/**
 * WhatsApp only accepts a freeform message inside 24 hours of the patient's
 * last inbound one. Outside it the send is refused server-side, so the inbox
 * has to say so before a staff member types. Null on channels with no such
 * window (portal, Telegram).
 */
export interface ThreadReplyWindow {
  open: boolean;
  /** When the current window closes; null when the patient never wrote. */
  expiresAt: string | null;
}

export interface ThreadContext {
  threadKey: string;
  channel: BotChannel;
  remoteId: string;
  lead: ThreadLeadDetail | null;
  handoffMode: 'bot' | 'human';
  optedOut: boolean;
  unverifiedBindingId: string | null;
  pendingBinding: PendingBindingDetail | null;
  isGuest: boolean;
  replyWindow: ThreadReplyWindow | null;
  /** Where a reply goes — the channel the patient last wrote from. */
  replyVia?: { channel: BotChannel; remoteId: string };
  /** Every channel this patient's conversation spans. */
  channels?: BotChannel[];
}

export type BotSenderKind = 'user' | 'bot' | 'system';

export interface BotMessage {
  id: string;
  threadKey: string;
  channel: BotChannel;
  direction: BotMessageDirection;
  body: string | null;
  status: BotMessageStatus;
  remoteId: string;
  patientId: string | null;
  createdAt: string;
  sentAt: string | null;
  deliveredAt: string | null;
  readAt: string | null;
  /**
   * For outbound rows: who initiated the send. NULL on historical
   * rows + every inbound row.
   */
  senderKind: BotSenderKind | null;
  /** Staff user id when senderKind === 'user'. */
  sentByUserId: string | null;
  /** Pre-resolved display string for outbound rows. */
  senderName: string | null;
  /** Message kind — 'media' marks an image a patient sent in the portal chat. */
  kind?: string;
  /** Why a send failed (`status === 'failed'`), e.g. OUTSIDE_SESSION_WINDOW. */
  failureReason?: string | null;
  /** Provider/portal metadata. Portal image messages carry imageStorageKey. */
  metadata?: {
    imageStorageKey?: string;
    /**
     * Set when this send failed on its own channel but the delivery cascade
     * got the message to the patient another way (email/SMS). `body` is what
     * actually went out — the SMS text differs from the WhatsApp one.
     */
    fallback?: { channel: 'email' | 'sms' | 'webpush'; body?: string | null };
  } | null;
}

/** How a channel reads in a sentence: "WhatsApp", "Telegram", "the portal". */
export function channelName(c: BotChannel | string): string {
  return c === 'whatsapp' ? 'WhatsApp' : c === 'telegram' ? 'Telegram' : 'the portal';
}

/** True for a portal image message (rendered via fetchCommunicationsMedia). */
export function isImageMessage(m: BotMessage): boolean {
  return m.kind === 'media' && Boolean(m.metadata?.imageStorageKey);
}

/** Fetch a portal chat image as an authenticated blob (for an object URL). */
export async function fetchCommunicationsMedia(messageId: string): Promise<Blob> {
  const res = await apiClient.get(`/communications/media/${messageId}`, {
    responseType: 'blob',
  });
  return res.data as Blob;
}

/** One outbound notification we sent the patient, with the channel it went out on. */
export interface PatientNotificationLog {
  id: string;
  type: string;
  category: string;
  title: string;
  nudgeChannel: 'webpush' | 'whatsapp' | 'email' | 'sms' | 'none' | null;
  createdAt: string;
}

/** Delivery log of nudges we sent a patient (incl. email/SMS/web-push). */
export async function listPatientNotifications(
  patientId: string,
): Promise<PatientNotificationLog[]> {
  const res = await apiClient.get('/communications/notifications', {
    params: { patientId },
  });
  return unwrap<PatientNotificationLog[]>(res.data);
}

export async function listThreads(opts?: {
  channel?: BotChannel;
  status?: 'unread' | 'all';
  segment?: InboxSegment;
  patientId?: string;
  cursor?: string;
  limit?: number;
}): Promise<{ threads: ThreadSummary[]; nextCursor: string | null }> {
  const res = await apiClient.get('/communications/threads', {
    params: {
      ...opts,
      limit: opts?.limit !== undefined ? String(opts.limit) : undefined,
    },
  });
  return unwrap(res.data);
}

export async function listThreadMessages(
  threadKey: string,
  limit = 100,
): Promise<BotMessage[]> {
  const res = await apiClient.get(
    `/communications/threads/${encodeURIComponent(threadKey)}/messages`,
    { params: { limit: String(limit) } },
  );
  return unwrap<BotMessage[]>(res.data);
}

export async function inboxUnreadCount(): Promise<number> {
  const res = await apiClient.get('/communications/unread-count');
  const payload = unwrap<{ count: number }>(res.data);
  return payload.count ?? 0;
}

export async function reply(input: {
  channel: BotChannel;
  remoteId: string;
  body: string;
}): Promise<{
  id: string;
  status: BotMessageStatus;
  sentAt: string | null;
  /**
   * The server persists a refused send as a FAILED row and answers 200 — a
   * reply is never lost, but it is not necessarily delivered. Callers MUST
   * check `status` instead of treating the resolved promise as "sent".
   */
  failureReason?: string | null;
}> {
  const res = await apiClient.post('/communications/reply', input);
  return unwrap(res.data);
}

export async function markThreadRead(
  threadKey: string,
): Promise<{ updated: number }> {
  const res = await apiClient.post(
    `/communications/threads/${encodeURIComponent(threadKey)}/mark-read`,
    {},
  );
  return unwrap(res.data);
}

// ---------- Thread actions (formerly the Leads-queue + Opt-outs pages) ----------
//
// Everything is addressed by thread key; the server resolves the underlying
// lead / opt-out / binding row. The caller never handles a lead id.

function threadUrl(threadKey: string, action: string): string {
  return `/communications/threads/${encodeURIComponent(threadKey)}/${action}`;
}

/** Count of threads in the "Needs you" segment — drives the nav badge. */
export async function inboxNeedsAttentionCount(): Promise<number> {
  const res = await apiClient.get('/communications/needs-attention-count');
  const payload = unwrap<{ count: number }>(res.data);
  return payload.count ?? 0;
}

export async function getThreadContext(
  threadKey: string,
): Promise<ThreadContext> {
  const res = await apiClient.get(threadUrl(threadKey, 'context'));
  return unwrap<ThreadContext>(res.data);
}

export async function bookThread(
  threadKey: string,
  input: BookLeadInput,
): Promise<{ patientId: string; appointmentId: string; consentId: string }> {
  const res = await apiClient.post(threadUrl(threadKey, 'book'), input);
  return unwrap(res.data);
}

export async function abandonThread(
  threadKey: string,
  reason?: string,
): Promise<unknown> {
  const res = await apiClient.post(threadUrl(threadKey, 'abandon'), { reason });
  return unwrap(res.data);
}

/** Per-contact kill switch for the conversational LLM. */
export async function setThreadLlmDisabled(
  threadKey: string,
  disabled: boolean,
): Promise<unknown> {
  const res = await apiClient.post(threadUrl(threadKey, 'llm-disabled'), {
    disabled,
  });
  return unwrap(res.data);
}

export async function optOutThread(
  threadKey: string,
  reason?: string,
): Promise<{ optedOut: boolean }> {
  const res = await apiClient.post(threadUrl(threadKey, 'opt-out'), { reason });
  return unwrap(res.data);
}

export async function optInThread(
  threadKey: string,
): Promise<{ cleared: boolean }> {
  const res = await apiClient.post(threadUrl(threadKey, 'opt-in'), {});
  return unwrap(res.data);
}

export async function approveThreadBinding(
  threadKey: string,
  targetPatientId?: string,
): Promise<unknown> {
  const res = await apiClient.post(threadUrl(threadKey, 'binding/approve'), {
    targetPatientId,
  });
  return unwrap(res.data);
}

export async function rejectThreadBinding(
  threadKey: string,
  reason?: string,
): Promise<unknown> {
  const res = await apiClient.post(threadUrl(threadKey, 'binding/reject'), {
    reason,
  });
  return unwrap(res.data);
}

/** Hold the thread for a human — suppresses bot auto-replies for a window. */
export async function takeOverThread(
  threadKey: string,
): Promise<{ handoffMode: 'human' }> {
  const res = await apiClient.post(threadUrl(threadKey, 'take-over'), {});
  return unwrap(res.data);
}
